import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { deviceApi } from '../api';
import { FleetEntry, controllerFamily, filterFleet, fleetLabel } from '../controllers/fleet';
import { DataTable } from '../table';
import type { DataTableColumn } from '../table';
import { useI18n } from '../i18n';
import { ControllerSnapshot, SnapshotDevice, snapshotFromSettings } from './peripherySnapshot';
import {
  FlowWizardAlertDest,
  FlowWizardArgs,
  FlowWizardBusDest,
  fieldBusesOf,
  snapshotDevicePoints,
} from './catalogFlowWizard';

type LayoutTarget = { layoutId: string; title: string; widgetId: string };
type AlertChannel = 'pushover' | 'mail';

type AlertDraft = {
  enable: boolean;
  min: string;
  max: string;
  channels: AlertChannel[];
};

const emptyAlertDraft = (): AlertDraft => ({
  enable: false,
  min: '',
  max: '',
  channels: ['pushover', 'mail'],
});

function parseBound(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

type FlowWizardDialogProps = {
  open: boolean;
  onClose: () => void;
  fleet: FleetEntry[];
  initialController?: FleetEntry | null;
  layoutTargets: LayoutTarget[];
  flowName: string;
  onInsert: (args: FlowWizardArgs) => void;
};

function labelValue(item: LayoutTarget): string {
  return `${item.layoutId}::${item.widgetId}`;
}

function parseLabel(value: string, targets: LayoutTarget[]): LayoutTarget | null {
  return targets.filter((item) => labelValue(item) === value)[0] || null;
}

const FlowWizardDialog: React.FC<FlowWizardDialogProps> = ({
  open,
  onClose,
  fleet,
  initialController,
  layoutTargets,
  flowName,
  onInsert,
}) => {
  const { t } = useI18n();
  const [step, setStep] = useState(0);
  const [controller, setController] = useState<FleetEntry | null>(null);
  const [snapshot, setSnapshot] = useState<ControllerSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [deviceId, setDeviceId] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [dashboard, setDashboard] = useState(true);
  const [labelByKey, setLabelByKey] = useState<Record<string, string>>({});
  const [busIds, setBusIds] = useState<string[]>([]);
  const [interval, setInterval] = useState(60);
  const [intervalMs, setIntervalMs] = useState(50);
  const [wireLocalActuators, setWireLocalActuators] = useState(true);
  const [wireZigbeeUplink, setWireZigbeeUplink] = useState(true);
  const [modernController, setModernController] = useState<FleetEntry | null>(null);
  const [modernOf, setModernOf] = useState('stacklight');
  const [alertByKey, setAlertByKey] = useState<Record<string, AlertDraft>>({});

  const steps = [
    t('flow.chainStepDevice'),
    t('flow.chainStepPeriphery'),
    t('flow.chainStepValues'),
    t('flow.chainStepDest'),
  ];

  useEffect(() => {
    if (!open) {
      return;
    }
    setStep(0);
    setController(initialController || null);
    setDeviceId('');
    setSelectedKeys([]);
    setDashboard(true);
    setLabelByKey({});
    setBusIds([]);
    setInterval(60);
    setIntervalMs(50);
    setSnapshot(null);
    setWireLocalActuators(true);
    setWireZigbeeUplink(true);
    setModernController(null);
    setModernOf('stacklight');
    setAlertByKey({});
  }, [open, initialController]);

  useEffect(() => {
    if (!open || !controller) {
      setSnapshot(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    deviceApi.getStoredFile(controller.id, '/settings.json')
      .then((stored) => {
        if (cancelled) {
          return;
        }
        const parsed = stored?.content ? JSON.parse(stored.content) : {};
        setSnapshot(snapshotFromSettings(parsed));
      })
      .catch(() => {
        if (!cancelled) {
          setSnapshot({ connections: [], devices: [] });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, controller]);

  const devices = snapshot?.devices || [];
  const device = devices.filter((item) => item.id === deviceId)[0] || null;
  const points = device ? snapshotDevicePoints(device) : [];
  const buses = snapshot ? fieldBusesOf(snapshot) : [];
  const hasPoll = selectedKeys.some((key) => (
    points.filter((point) => point.key === key && point.kind === 'poll').length > 0
  ));

  useEffect(() => {
    const found = (snapshot?.devices || []).filter((item) => item.id === deviceId)[0];
    setSelectedKeys(found ? snapshotDevicePoints(found).map((point) => point.key) : []);
  }, [deviceId, snapshot]);

  const peripheryColumns = useMemo((): DataTableColumn<SnapshotDevice>[] => [
    { id: 'id', label: t('flow.chainPeripheryCol'), accessor: (row) => row.id, filter: 'none' },
    { id: 'type', label: t('flow.chainTypeCol'), accessor: (row) => t(`flow.types.${row.type}`), filter: 'none' },
  ], [t]);

  const valueColumns = useMemo((): DataTableColumn<{ key: string; kind: string }>[] => [
    { id: 'key', label: t('flow.chainValueCol'), accessor: (row) => row.key, filter: 'none' },
    {
      id: 'kind',
      label: t('flow.chainKindCol'),
      accessor: (row) => row.kind,
      filter: 'none',
      render: (row) => t(
        row.kind === 'event'
          ? 'flow.chainKindEvent'
          : row.kind === 'command'
            ? 'flow.chainKindCommand'
            : 'flow.chainKindPoll',
      ),
    },
  ], [t]);

  const labels = selectedKeys
    .map((key) => {
      const picked = parseLabel(labelByKey[key] || '', layoutTargets);
      if (!picked) {
        return null;
      }
      return { key, layoutId: picked.layoutId, widgetId: picked.widgetId };
    })
    .filter((item): item is { key: string; layoutId: string; widgetId: string } => Boolean(item));

  const selectedBuses: FlowWizardBusDest[] = buses.filter((bus) => busIds.indexOf(bus.of) >= 0);
  const isGpioInput = device?.type === 'gpio_input';
  const hasLegacyDest = isGpioInput && (
    wireLocalActuators || wireZigbeeUplink || Boolean(modernController)
  );

  const alertDests: FlowWizardAlertDest[] = selectedKeys.map((key) => {
    const draft = alertByKey[key] || emptyAlertDraft();
    const min = parseBound(draft.min);
    const max = parseBound(draft.max);
    const channels = (draft.channels || []).filter((c): c is AlertChannel => (
      c === 'pushover' || c === 'mail'
    ));
    return {
      key,
      enable: draft.enable,
      min,
      max,
      channels: channels.length ? channels : ['pushover', 'mail'],
    };
  });
  const enabledAlerts = alertDests.filter((item) => (
    item.enable && (item.min != null || item.max != null) && item.channels.length > 0
  ));
  const alertIncomplete = alertDests.some((item) => {
    if (!item.enable) {
      return false;
    }
    return (item.min == null && item.max == null) || item.channels.length === 0;
  });

  const hasDest = dashboard
    || labels.length > 0
    || selectedBuses.length > 0
    || hasLegacyDest
    || enabledAlerts.length > 0;

  const canNext = step === 0
    ? Boolean(controller)
    : step === 1
      ? Boolean(device)
      : step === 2
        ? selectedKeys.length > 0
        : hasDest && !alertIncomplete;

  const patchAlert = (key: string, patch: Partial<AlertDraft>) => {
    setAlertByKey((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || emptyAlertDraft()), ...patch },
    }));
  };

  const toggleAlertChannel = (key: string, channel: AlertChannel, checked: boolean) => {
    const draft = alertByKey[key] || emptyAlertDraft();
    const next = checked
      ? draft.channels.concat(channel).filter((c, i, arr) => arr.indexOf(c) === i)
      : draft.channels.filter((c) => c !== channel);
    patchAlert(key, { channels: next });
  };

  const finish = () => {
    if (!controller || !device || !snapshot || !canNext) {
      return;
    }
    onInsert({
      flowName,
      hostname: controller.hostname,
      controllerId: controller.id,
      device,
      keys: selectedKeys,
      dashboard: dashboard || Boolean(modernController),
      labels,
      buses: selectedBuses,
      alerts: enabledAlerts,
      snapshot,
      interval: hasPoll && !isGpioInput ? interval : undefined,
      intervalMs: isGpioInput ? intervalMs : undefined,
      wireLocalActuators: isGpioInput ? wireLocalActuators : undefined,
      wireZigbeeUplink: device.type === 'stacklight'
        ? snapshot.zigbee === true
        : (isGpioInput ? wireZigbeeUplink : undefined),
      modernHostname: modernController?.hostname,
      modernControllerId: modernController?.id,
      modernOf: modernController ? (modernOf.trim() || 'stacklight') : undefined,
    });
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('flow.chainWizardTitle')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <Stepper activeStep={step} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
        {loading && <CircularProgress size={24} sx={{ mx: 'auto', my: 1 }} />}
        {!loading && step === 0 && (
          <Autocomplete
            options={fleet}
            value={controller}
            onChange={(_, next) => {
              setController(next);
              setDeviceId('');
            }}
            filterOptions={(options, state) => filterFleet(options, state.inputValue)}
            getOptionLabel={(option) => fleetLabel(option)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            groupBy={(option) => t('deviceConfig.familyGroup', {
              family: controllerFamily(option.hostname),
              status: option.online ? t('common.online') : t('common.offline'),
            })}
            noOptionsText={t('deviceConfig.noControllerHits')}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label={t('flow.controller')}
                placeholder={t('flow.chainPickController')}
                helperText={t('deviceConfig.controllerSearchHint')}
              />
            )}
          />
        )}
        {!loading && step === 1 && (
          devices.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{t('flow.chainNoPeriphery')}</Typography>
          ) : (
            <DataTable
              rows={devices}
              columns={peripheryColumns}
              getRowId={(row) => row.id}
              selectedId={deviceId}
              onRowClick={(row) => setDeviceId(row.id)}
              toolbar={false}
              dense
              maxHeight={280}
              emptyMessage={t('flow.chainNoPeriphery')}
            />
          )
        )}
        {!loading && step === 2 && (
          points.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{t('flow.chainNoValues')}</Typography>
          ) : (
            <>
              <DataTable
                rows={points}
                columns={valueColumns}
                getRowId={(row) => row.key}
                selectedIds={selectedKeys}
                onSelectedIdsChange={setSelectedKeys}
                toolbar={false}
                dense
                maxHeight={280}
                emptyMessage={t('flow.chainNoValues')}
              />
              {hasPoll && !isGpioInput && (
                <TextField
                  size="small"
                  type="number"
                  label={t('flow.intervalSec')}
                  value={interval}
                  onChange={(event) => setInterval(Number(event.target.value) || 60)}
                />
              )}
              {isGpioInput && (
                <TextField
                  size="small"
                  type="number"
                  label={t('flow.intervalMs')}
                  value={intervalMs}
                  onChange={(event) => setIntervalMs(Math.max(10, Number(event.target.value) || 50))}
                  helperText={t('flow.intervalMsHint')}
                />
              )}
            </>
          )
        )}
        {!loading && step === 3 && (
          <>
            <Typography variant="body2" color="text.secondary">{t('flow.chainDestHint')}</Typography>
            {isGpioInput && (
              <>
                <Typography variant="subtitle2">{t('flow.chainDestLegacy')}</Typography>
                <FormGroup>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={wireLocalActuators}
                        onChange={(event) => setWireLocalActuators(event.target.checked)}
                      />
                    )}
                    label={t('flow.chainDestLocalActuators')}
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={wireZigbeeUplink}
                        onChange={(event) => setWireZigbeeUplink(event.target.checked)}
                      />
                    )}
                    label={t('flow.chainDestZigbeeMqtt')}
                  />
                </FormGroup>
                <Autocomplete
                  options={fleet.filter((entry) => entry.id !== controller?.id)}
                  value={modernController}
                  onChange={(_, next) => setModernController(next)}
                  filterOptions={(options, state) => filterFleet(options, state.inputValue)}
                  getOptionLabel={(option) => fleetLabel(option)}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label={t('flow.chainDestModern')}
                      placeholder={t('flow.chainPickController')}
                      helperText={t('flow.chainDestModernHint')}
                    />
                  )}
                />
                {modernController && (
                  <TextField
                    size="small"
                    label={t('flow.chainDestModernOf')}
                    value={modernOf}
                    onChange={(event) => setModernOf(event.target.value)}
                    helperText={t('flow.chainDestModernOfHint')}
                  />
                )}
              </>
            )}
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={dashboard} onChange={(event) => setDashboard(event.target.checked)} />}
                label={t('flow.chainDestDashboard')}
              />
            </FormGroup>
            <Typography variant="subtitle2">{t('flow.chainDestLabel')}</Typography>
            {layoutTargets.length === 0 && (
              <Typography variant="caption" color="text.secondary">{t('flow.chainNoLayouts')}</Typography>
            )}
            {selectedKeys.map((key) => (
              <Autocomplete
                key={key}
                options={layoutTargets}
                value={parseLabel(labelByKey[key] || '', layoutTargets)}
                onChange={(_, next) => {
                  setLabelByKey((prev) => {
                    const copy = { ...prev };
                    if (next) {
                      copy[key] = labelValue(next);
                    } else {
                      delete copy[key];
                    }
                    return copy;
                  });
                }}
                getOptionLabel={(item) => `${item.title || item.layoutId} · ${item.widgetId}`}
                isOptionEqualToValue={(a, b) => labelValue(a) === labelValue(b)}
                renderInput={(params) => (
                  <TextField {...params} size="small" label={t('flow.chainLabelFor', { key })} />
                )}
              />
            ))}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>{t('flow.chainDestBus')}</Typography>
            {buses.length === 0 ? (
              <Typography variant="caption" color="text.secondary">{t('flow.chainNoBuses')}</Typography>
            ) : (
              <FormGroup>
                {buses.map((bus) => (
                  <FormControlLabel
                    key={bus.of}
                    control={(
                      <Checkbox
                        checked={busIds.indexOf(bus.of) >= 0}
                        onChange={(event) => {
                          setBusIds((prev) => (
                            event.target.checked
                              ? prev.concat(bus.of).filter((id, index, ids) => ids.indexOf(id) === index)
                              : prev.filter((id) => id !== bus.of)
                          ));
                        }}
                      />
                    )}
                    label={`${bus.busType} · ${bus.label}`}
                  />
                ))}
              </FormGroup>
            )}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>{t('flow.chainDestAlert')}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {t('flow.chainDestAlertHint')}
            </Typography>
            {selectedKeys.map((key) => {
              const draft = alertByKey[key] || emptyAlertDraft();
              return (
                <Box key={`alert-${key}`} sx={{ mb: 1.5, pl: 0.5, borderLeft: 2, borderColor: draft.enable ? 'warning.main' : 'divider' }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={draft.enable}
                        onChange={(event) => patchAlert(key, { enable: event.target.checked })}
                      />
                    )}
                    label={t('flow.chainAlertFor', { key })}
                  />
                  {draft.enable && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 4, pb: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <TextField
                          size="small"
                          type="number"
                          label={t('alerting.min')}
                          value={draft.min}
                          onChange={(event) => patchAlert(key, { min: event.target.value })}
                          sx={{ width: 120 }}
                        />
                        <TextField
                          size="small"
                          type="number"
                          label={t('alerting.max')}
                          value={draft.max}
                          onChange={(event) => patchAlert(key, { max: event.target.value })}
                          sx={{ width: 120 }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('flow.chainAlertChannels')}
                      </Typography>
                      <FormGroup row>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={draft.channels.indexOf('pushover') >= 0}
                              onChange={(event) => toggleAlertChannel(key, 'pushover', event.target.checked)}
                            />
                          )}
                          label={t('alerting.pushoverEnable')}
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={draft.channels.indexOf('mail') >= 0}
                              onChange={(event) => toggleAlertChannel(key, 'mail', event.target.checked)}
                            />
                          )}
                          label={t('alerting.mailEnable')}
                        />
                      </FormGroup>
                    </Box>
                  )}
                </Box>
              );
            })}
            {alertIncomplete && (
              <Alert severity="warning">{t('flow.chainAlertIncomplete')}</Alert>
            )}
            {!hasDest && (
              <Alert severity="info">{t('flow.chainNeedDest')}</Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => setStep((n) => n - 1)} disabled={step === 0 || loading}>
          {t('deviceConfig.wizard.back')}
        </Button>
        {step < 3 ? (
          <Button variant="contained" onClick={() => setStep((n) => n + 1)} disabled={!canNext || loading}>
            {t('deviceConfig.wizard.next')}
          </Button>
        ) : (
          <Button variant="contained" onClick={finish} disabled={!canNext || loading}>
            {t('flow.chainFinish')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FlowWizardDialog;
